/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import DOMPurify from 'dompurify';

const CONFIG = { USE_PROFILES: { html: true } };

const SCHEME = /^[a-z][a-z0-9+.-]*:/i;
const OWN_HOST = /(^|\.)ere-vergelijk\.nl$/i;

/** A link to our own site: relative, a fragment, or an absolute URL on ere-vergelijk.nl or a subdomain. */
export function isInternalHref(href: string): boolean {
  const h = href.trim();
  if (!h) return false;
  if (!SCHEME.test(h) && !h.startsWith('//')) return true;
  try {
    const url = new URL(h, 'https://ere-vergelijk.nl');
    return (url.protocol === 'https:' || url.protocol === 'http:') && OWN_HOST.test(url.hostname);
  } catch {
    return false;
  }
}

// The CMS editor's link extension (TipTap) stamps `rel="noopener noreferrer
// nofollow"` on every link it makes, our own pages included, and that is what
// sits in the stored content on every environment. A nofollow on an internal
// link tells search engines not to pass anything along our own site, so it is
// dropped here, at the one gate every piece of CMS HTML passes on its way to a
// page (the bake included). External links keep theirs. Imported without a
// window (a node-environment test), DOMPurify is a stub without addHook — and
// without a working sanitize; the bake installs its window before this loads.
if (typeof DOMPurify.addHook === 'function') {
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (node.nodeName !== 'A') return;
    const rel = node.getAttribute('rel');
    if (!rel || !isInternalHref(node.getAttribute('href') ?? '')) return;
    const kept = rel.split(/\s+/).filter((token) => token && token.toLowerCase() !== 'nofollow');
    if (kept.length) node.setAttribute('rel', kept.join(' '));
    else node.removeAttribute('rel');
  });
}

// DOMPurify parses its input into a document on every call, and the same CMS
// strings (FAQ answers, info sections, article blocks) were sanitized again on
// every render: ~0.13 s of main thread on a phone profile of the homepage at
// 4x CPU slowdown (2026-10-02). Under one fixed config the result depends on
// the input alone, so the browser sanitizes each distinct string once.
//
// Browser only, and bounded: the server bakes every page inside its own
// process (512 MB on prd, OOM-killed once on 2026-10-02), where a cache of
// every article block would only cost memory.
const cache = new Map<string, string>();
const CACHE_MAX_ENTRIES = 300;
const CACHE_MAX_CHARS = 500_000;
let cachedChars = 0;

/** `DOMPurify.sanitize(raw, { USE_PROFILES: { html: true } })`, memoised in the browser. */
export function sanitizeHtml(raw: string): string {
  if (import.meta.env.SSR) return String(DOMPurify.sanitize(raw, CONFIG));
  const hit = cache.get(raw);
  if (hit !== undefined) return hit;
  const clean = String(DOMPurify.sanitize(raw, CONFIG));
  const size = raw.length + clean.length;
  if (size > CACHE_MAX_CHARS) return clean;
  if (cache.size >= CACHE_MAX_ENTRIES || cachedChars + size > CACHE_MAX_CHARS) {
    cache.clear();
    cachedChars = 0;
  }
  cache.set(raw, clean);
  cachedChars += size;
  return clean;
}
